// src/utils/validation.ts

import { MESSAGES } from './messages';

const V = MESSAGES.validation;

const REGEX_PATTERNS = {
  email: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
  phone: '^(\\+44|0)?[0-9]{10,11}$',
  password: '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[!@#$%^&*])[A-Za-z\\d!@#$%^&*]{8,}$',
  otp: '^\\d{6}$',
  name: "^[a-zA-Z\\s'-]{2,100}$",
  medicalLicence: '^[A-Za-z0-9-]{4,20}$',
} as const;

export const REGEX = {
  email: new RegExp(REGEX_PATTERNS.email),
  phone: new RegExp(REGEX_PATTERNS.phone),
  password: new RegExp(REGEX_PATTERNS.password),
  otp: new RegExp(REGEX_PATTERNS.otp),
  name: new RegExp(REGEX_PATTERNS.name),
  medicalLicence: new RegExp(REGEX_PATTERNS.medicalLicence),
} as const;

export type ValidatorFn = (
  value: string,
  allValues: Record<string, string>,
) => string | undefined;

export type ValidationSchema = Record<string, ValidatorFn[]>;

export const isRequired =
  (label: string): ValidatorFn =>
  (value) =>
    value.trim() ? undefined : V.required(label);

export const isValidEmail: ValidatorFn = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return REGEX.email.test(trimmed) ? undefined : V.invalidEmail;
};

export const isValidPhone: ValidatorFn = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return REGEX.phone.test(trimmed) ? undefined : V.invalidPhone;
};

export const isNotFutureDate: ValidatorFn = (value) => {
  if (!value) return undefined;
  const date = new Date(value);
  if (isNaN(date.getTime())) return V.invalidDateFormat;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date > today ? V.futureDate : undefined;
};

export const isValidPassword: ValidatorFn = (value) => {
  if (!value) return V.invalidPassword.missing;
  if (value.length < 8) return V.invalidPassword.minLength;
  if (!/[A-Z]/.test(value)) return V.invalidPassword.uppercase;
  if (!/[a-z]/.test(value)) return V.invalidPassword.lowercase;
  if (!/[0-9]/.test(value)) return V.invalidPassword.number;
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(value)) return V.invalidPassword.specialChar;
  return undefined;
};

export const isValidOtp: ValidatorFn = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return REGEX.otp.test(trimmed) ? undefined : V.invalidOtp;
};

export const isValidName: ValidatorFn = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return REGEX.name.test(trimmed) ? undefined : V.invalidName;
};

export const isValidLicence: ValidatorFn = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return REGEX.medicalLicence.test(trimmed) ? undefined : V.invalidLicence;
};

export const matchesField =
  (targetField: string, label: string): ValidatorFn =>
  (value, allValues) => {
    if (!value) return undefined;
    return value === allValues[targetField] ? undefined : V.passwordMismatch;
  };

export function validate(
  schema: ValidationSchema,
  values: Record<string, string>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const [field, validators] of Object.entries(schema)) {
    for (const validator of validators) {
      const msg = validator(values[field] ?? '', values);
      if (msg) {
        errors[field] = msg;
        break;
      }
    }
  }
  return errors;
}

export function sanitizeInput(value: string): string {
  return value
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
}
